import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import COUNTRIES from '../countries.json';

// Work authorization options
const WORK_AUTH_OPTIONS = [
    { id: 'us_citizen', label: 'US Citizen' },
    { id: 'us_green_card', label: 'US Green Card' },
    { id: 'us_h1b', label: 'US H1B' },
    { id: 'us_h1b_transfer', label: 'US H1B Transfer Possible' },
    { id: 'us_opt', label: 'US OPT' },
    { id: 'us_cpt', label: 'US CPT' },
    { id: 'us_tn', label: 'US TN' },
    { id: 'us_l1', label: 'US L1' },
    { id: 'uk_citizen', label: 'UK Citizen' },
    { id: 'uk_work_permit', label: 'UK Work Permit' },
    { id: 'eu_citizen', label: 'EU Citizen' },
    { id: 'eu_work_permit', label: 'EU Work Permit' },
    { id: 'canada_citizen', label: 'Canadian Citizen' },
    { id: 'canada_pr', label: 'Canadian PR' },
    { id: 'canada_work_permit', label: 'Canadian Work Permit' },
    { id: 'india_citizen', label: 'Indian Citizen' },
    { id: 'australia_citizen', label: 'Australian Citizen' },
    { id: 'australia_pr', label: 'Australian PR' },
    { id: 'remote_only', label: 'Remote Only (No visa required)' },
];

// Mercor-style input classes
const inputStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '6px',
        fontSize: '0.875rem',
        '& fieldset': {
            borderColor: '#d1d5db',
        },
        '&:hover fieldset': {
            borderColor: '#d1d5db',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#4f46e5',
            borderWidth: '2px',
        },
    },
    '& .MuiInputBase-input': {
        padding: '8px 12px',
        fontSize: '0.875rem',
    },
    '& .MuiInputLabel-root': {
        fontSize: '0.875rem',
    },
};

const FormSection = ({ title, children, sidebarWidth = '200px' }) => (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 5 }, mb: 4 }}>
        <Box sx={{ width: { md: sidebarWidth }, flexShrink: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>
                {title}
            </Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
            {children}
        </Box>
    </Box>
);

const LocationWorkAuthTab = ({ userProfile, userId, authAxios, onUpdate, setError }) => {
    const [savingLocation, setSavingLocation] = useState(false);
    const [savingWorkAuth, setSavingWorkAuth] = useState(false);

    const [formData, setFormData] = useState({
        country: '',
        state: '',
        city: '',
        postal_code: '',
    });

    const [workAuth, setWorkAuth] = useState([]);

    // Initialize form data from userProfile
    useEffect(() => {
        if (userProfile) {
            setFormData({
                country: userProfile.country || '',
                state: userProfile.state || '',
                city: userProfile.city || '',
                postal_code: userProfile.postal_code || '',
            });
            setWorkAuth(userProfile.work_authorization || []);
        }
    }, [userProfile]);

    // Handle input changes
    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // Handle work auth checkbox changes
    const handleWorkAuthChange = (authId) => {
        setWorkAuth(prev => 
            prev.includes(authId) 
                ? prev.filter(id => id !== authId)
                : [...prev, authId]
        );
    };

    // Save Location section handler
    const handleSaveLocation = async () => {
        setSavingLocation(true);
        try {
            await authAxios.patch('/self/', {
                country: formData.country,
                state: formData.state,
                city: formData.city,
                postal_code: formData.postal_code,
            });

            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Failed to save location:', error);
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.response?.data?.error || 'Failed to save location changes';
            if (setError) setError(errorMsg);
        } finally {
            setSavingLocation(false);
        }
    };

    // Save Work Authorization section handler
    const handleSaveWorkAuth = async () => {
        setSavingWorkAuth(true);
        try {
            await authAxios.patch('/self/', {
                work_authorization: workAuth,
            });

            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Failed to save work authorization:', error);
            const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.response?.data?.error || 'Failed to save work authorization';
            if (setError) setError(errorMsg);
        } finally {
            setSavingWorkAuth(false);
        }
    };

    return (
        <Box sx={{ maxWidth: '900px', mx: 'auto', py: 4 }}>
            {/* Location Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                    Location
                </Typography>
                <Divider sx={{ mb: 4 }} />
                
                <FormSection title="Country">
                    <Select
                        fullWidth
                        size="small"
                        value={formData.country}
                        onChange={(e) => handleInputChange('country', e.target.value)}
                        displayEmpty
                        sx={{
                            ...inputStyles['& .MuiOutlinedInput-root'],
                            fontSize: '0.875rem',
                        }}
                        IconComponent={ExpandMoreIcon}
                    >
                        <MenuItem value="" disabled>
                            <Typography sx={{ color: '#9ca3af' }}>Select Country</Typography>
                        </MenuItem>
                        {COUNTRIES.map(country => (
                            <MenuItem key={country} value={country}>{country}</MenuItem>
                        ))}
                    </Select>
                </FormSection>

                <FormSection title="State">
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Ex: California"
                        value={formData.state}
                        onChange={(e) => handleInputChange('state', e.target.value)}
                        sx={inputStyles}
                    />
                </FormSection>

                <FormSection title="City">
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Ex: San Francisco"
                        value={formData.city}
                        onChange={(e) => handleInputChange('city', e.target.value)}
                        sx={inputStyles}
                    />
                </FormSection>

                <FormSection title="Postal Code">
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Ex: 94105"
                        value={formData.postal_code}
                        onChange={(e) => handleInputChange('postal_code', e.target.value)}
                        sx={inputStyles}
                    />
                </FormSection>

                {/* Save Button for Location */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={handleSaveLocation}
                        disabled={savingLocation}
                        sx={{
                            bgcolor: '#4f46e5',
                            textTransform: 'none',
                            px: 4,
                            py: 1,
                            borderRadius: '8px',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#4338ca' },
                        }}
                    >
                        {savingLocation ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save Changes'}
                    </Button>
                </Box>
            </Box>

            {/* Work Authorization Section */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h6" sx={{ fontSize: '1.5rem', fontWeight: 600, color: '#111827', mb: 1 }}>
                    Work Authorization
                </Typography>
                <Divider sx={{ mb: 4 }} />
                
                <FormSection title="Work Authorization">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {WORK_AUTH_OPTIONS.map((option) => (
                            <FormControlLabel
                                key={option.id}
                                control={
                                    <Checkbox
                                        checked={workAuth.includes(option.id)}
                                        onChange={() => handleWorkAuthChange(option.id)}
                                        sx={{
                                            color: '#d1d5db',
                                            '&.Mui-checked': { color: '#4f46e5' },
                                        }}
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#374151' }}>
                                        {option.label}
                                    </Typography>
                                }
                            />
                        ))}
                    </Box>
                </FormSection>

                {/* Save Button for Work Authorization */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={handleSaveWorkAuth}
                        disabled={savingWorkAuth}
                        sx={{
                            bgcolor: '#4f46e5',
                            textTransform: 'none',
                            px: 4,
                            py: 1,
                            borderRadius: '8px',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#4338ca' },
                        }}
                    >
                        {savingWorkAuth ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save Changes'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default LocationWorkAuthTab;
